/* eslint no-param-reassign: 0 */
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import IntegrationsAPI from '../../api/integrations';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import { dedupeInFlight } from 'dashboard/store/utils/dedupeInFlight';

const state = {
  records: [],
  accountId: null,
  generation: 0,
  requests: {},
  uiFlags: {
    isCreating: false,
    isFetching: false,
    isFetchingItem: false,
    isUpdating: false,
    isCreatingHook: false,
    isDeletingHook: false,
    isCreatingSlack: false,
    isUpdatingSlack: false,
    isFetchingSlackChannels: false,
  },
};

const belongsToCurrentAccount = ($state, rootGetters) => {
  const currentId = rootGetters?.getCurrentAccountId;
  if (currentId == null || $state.accountId == null) return true;
  return String($state.accountId) === String(currentId);
};

// Each request belongs to an account generation. Returning to the same account
// does not make a response from a previous visit current again.
const beginRequest = (context, operation) => {
  const { commit, state: current = state } = context;
  const accountId = IntegrationsAPI.accountIdFromRoute;
  commit('SYNC_ACCOUNT', accountId);
  commit('START_REQUEST', operation);
  const generation = current.generation;
  const requestId = current.requests[operation];
  return {
    current: () =>
      current.generation === generation &&
      IntegrationsAPI.accountIdFromRoute === accountId,
    latest: () => current.requests[operation] === requestId,
  };
};

const mutate = async (context, operation, flag, request, onSuccess) => {
  const guard = beginRequest(context, operation);
  context.commit(types.default.SET_INTEGRATIONS_UI_FLAG, { [flag]: true });
  try {
    const response = await request();
    if (!guard.current()) return;
    if (onSuccess) onSuccess(response);
  } catch (error) {
    if (guard.current()) throw error;
  } finally {
    if (guard.current() && guard.latest()) {
      context.commit(types.default.SET_INTEGRATIONS_UI_FLAG, { [flag]: false });
    }
  }
};

export const getters = {
  getAppIntegrations($state, _getters, _rootState, rootGetters) {
    if (!belongsToCurrentAccount($state, rootGetters)) return [];
    return $state.records;
  },
  getIntegration:
    ($state, _getters, _rootState, rootGetters) =>
    (integrationId, defaultValue = {}) => {
      if (!belongsToCurrentAccount($state, rootGetters)) return defaultValue;
      const [integration] = $state.records.filter(
        record => record.id === integrationId
      );
      return integration || defaultValue;
    },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  // ContactPanel.vue (conversation open) and VideoCallButton/useLabelSuggestions
  // dispatch this independently — the action ignores its payload and always
  // fetches every integration, so concurrent callers of the same account and
  // generation are truly identical GETs. The cache key includes account and
  // generation so A and B do not share a request, and A-B-A does not reuse
  // the first visit's in-flight GET. Account switches call this from the
  // dashboard shell (useAccountBootstrapData) and from the integrations screens.
  get: context => {
    const { commit, state: current = state } = context;
    const accountId = IntegrationsAPI.accountIdFromRoute;
    commit('SYNC_ACCOUNT', accountId);
    const generation = current.generation;
    return dedupeInFlight(
      `integrations/get:${accountId}:${generation}`,
      async () => {
        commit('START_REQUEST', 'Fetching');
        const requestId = current.requests.Fetching;
        const isCurrent = () =>
          current.generation === generation &&
          IntegrationsAPI.accountIdFromRoute === accountId;
        const isLatest = () => current.requests.Fetching === requestId;
        commit(types.default.SET_INTEGRATIONS_UI_FLAG, { isFetching: true });
        try {
          const response = await IntegrationsAPI.get();
          if (!isCurrent() || !isLatest()) return;
          commit(types.default.SET_INTEGRATIONS, response.data.payload);
        } catch (error) {
          // Preserve the original get() contract: errors are swallowed.
        } finally {
          if (isCurrent() && isLatest()) {
            commit(types.default.SET_INTEGRATIONS_UI_FLAG, {
              isFetching: false,
            });
          }
        }
      }
    );
  },

  connectSlack: async (context, code) => {
    try {
      await mutate(
        context,
        'CreatingSlack',
        'isCreatingSlack',
        () => IntegrationsAPI.connectSlack(code),
        response => {
          context.commit(types.default.ADD_INTEGRATION, response.data);
        }
      );
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  updateSlack: async (context, slackObj) => {
    try {
      await mutate(
        context,
        'UpdatingSlack',
        'isUpdatingSlack',
        () => IntegrationsAPI.updateSlack(slackObj),
        response => {
          context.commit(types.default.ADD_INTEGRATION, response.data);
        }
      );
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  listAllSlackChannels: async context => {
    const guard = beginRequest(context, 'FetchingSlackChannels');
    context.commit(types.default.SET_INTEGRATIONS_UI_FLAG, {
      isFetchingSlackChannels: true,
    });
    try {
      const response = await IntegrationsAPI.listAllSlackChannels();
      if (!guard.current()) return null;
      return response.data;
    } catch (error) {
      if (guard.current() && guard.latest()) {
        context.commit(types.default.SET_INTEGRATIONS_UI_FLAG, {
          isFetchingSlackChannels: false,
        });
      }
    }
    return null;
  },

  deleteIntegration: async (context, integrationId) => {
    try {
      await mutate(
        context,
        'Deleting',
        'isDeleting',
        () => IntegrationsAPI.delete(integrationId),
        () => {
          context.commit(types.default.DELETE_INTEGRATION, {
            id: integrationId,
            enabled: false,
          });
        }
      );
    } catch (error) {
      // Preserve the original deleteIntegration contract: errors are swallowed.
    }
  },
  // Inherited action. IntegrationsAPI has no showHook method; callers that
  // dispatch this still throw. Not a validated product path.
  showHook: async (context, hookId) => {
    try {
      await mutate(
        context,
        'FetchingItem',
        'isFetchingItem',
        () => IntegrationsAPI.showHook(hookId),
        response => {
          context.commit(types.default.ADD_INTEGRATION_HOOKS, response.data);
        }
      );
    } catch (error) {
      throw new Error(error);
    }
  },
  createHook: async (context, hookData) => {
    await mutate(
      context,
      'CreatingHook',
      'isCreatingHook',
      () => IntegrationsAPI.createHook(hookData),
      response => {
        context.commit(types.default.ADD_INTEGRATION_HOOKS, response.data);
      }
    );
  },
  deleteHook: async (context, { appId, hookId }) => {
    try {
      await mutate(
        context,
        'DeletingHook',
        'isDeletingHook',
        () => IntegrationsAPI.deleteHook(hookId),
        () => {
          context.commit(types.default.DELETE_INTEGRATION_HOOKS, {
            appId,
            hookId,
          });
        }
      );
    } catch (error) {
      throw new Error(error);
    }
  },
};

export const mutations = {
  SYNC_ACCOUNT(current, accountId) {
    if (current.accountId === accountId) return;
    current.accountId = accountId;
    current.generation += 1;
    current.records = [];
    current.requests = {};
    Object.keys(current.uiFlags).forEach(key => {
      current.uiFlags[key] = false;
    });
  },
  START_REQUEST(current, operation) {
    current.requests[operation] = (current.requests[operation] || 0) + 1;
  },
  [types.default.SET_INTEGRATIONS_UI_FLAG]($state, uiFlag) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlag };
  },
  [types.default.SET_INTEGRATIONS]: MutationHelpers.set,
  [types.default.ADD_INTEGRATION]: MutationHelpers.updateAttributes,
  [types.default.DELETE_INTEGRATION]: MutationHelpers.updateAttributes,
  [types.default.ADD_INTEGRATION_HOOKS]: ($state, data) => {
    $state.records = $state.records.map(record => {
      if (record.id === data.app_id) {
        return {
          ...record,
          hooks: [...record.hooks, data],
        };
      }
      return record;
    });
  },
  [types.default.DELETE_INTEGRATION_HOOKS]: ($state, { appId, hookId }) => {
    $state.records = $state.records.map(record => {
      if (record.id === appId) {
        return {
          ...record,
          hooks: record.hooks.filter(hook => hook.id !== hookId),
        };
      }
      return record;
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
